import {commitUpdateIssueProjectsMutation} from '@github-ui/item-picker/commitUpdateIssueProjectsMutation'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {type HTMLAttributes, type ReactNode, useCallback, useMemo, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'
import {ConnectionHandler} from 'relay-runtime'

import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {commitDeleteIssueProjectsMutation} from '../../mutations/delete-issue-projects-mutation'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {
  ProjectsSectionFragment$data,
  ProjectsSectionFragment$key,
} from './__generated__/ProjectsSectionFragment.graphql'
import {ProjectItemSection} from './ProjectItemSection'
import styles from './ProjectsSection.module.css'

const MAX_VISIBLE_ITEMS = 5
const PROJECT_OVERFLOW_CONTAINER_ID = 'sidebar-projects-overflow-items'

const ReadonlyProjectsSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.projects} />
const ProjectsSectionHeader = ({ref, ...props}: HTMLAttributes<HTMLElement> & {ref?: React.Ref<HTMLButtonElement>}) => (
  <SectionHeader ref={ref} id={props.id} title={LABELS.sectionTitles.projects} buttonProps={props} />
)
ProjectsSectionHeader.displayName = 'ProjectsSectionHeader'

type ProjectsSectionProps = {
  sectionHeader: React.ReactElement
  hasItems: boolean
  children?: ReactNode
}
const ProjectsSection = ({sectionHeader, hasItems, children}: ProjectsSectionProps) => {
  return (
    <Section
      id={`sidebar-projects-section`}
      sectionHeader={sectionHeader}
      emptyText={hasItems ? undefined : LABELS.emptySections.projects}
    >
      {children}
    </Section>
  )
}

export const ProjectsSectionFallback = () => (
  <Section
    id={'projects-section-fallback'}
    sectionHeader={<ReadonlySectionHeader title={ERRORS.projectsUnavailable} />}
    emptyText={ERRORS.tryAgainLater}
  />
)

type CreateIssueProjectsSectionProps = {
  owner: string
  repo: string
  projects: ProjectPickerProject[]
  readonly: boolean
  onSelectionChange: (value: ProjectPickerProject[]) => void
}
export const CreateIssueProjectsSection = ({
  owner,
  repo,
  projects,
  readonly,
  onSelectionChange,
  ...sharedConfigProps
}: CreateIssueProjectsSectionProps) => {
  const headingLabelId = 'create-issue-sidebar-projects-section-heading'
  const sectionHeader = readonly ? (
    <ReadonlyProjectsSectionHeader />
  ) : (
    <ProjectPicker
      pickerId={'create-issue-projects-picker'}
      owner={owner}
      repo={repo}
      selectedProjects={projects}
      readonly={readonly}
      onSave={onSelectionChange}
      anchorElement={props => <ProjectsSectionHeader {...props} id={headingLabelId} />}
      {...sharedConfigProps}
    />
  )

  return (
    <ErrorBoundary fallback={<ProjectsSectionFallback />}>
      <ProjectsSection sectionHeader={sectionHeader} hasItems={projects.length > 0}>
        {projects.length > 0 && (
          <ActionList aria-labelledby={headingLabelId} variant="full" className={styles.projectActionList}>
            {projects.map(project => (
              <ActionList.LinkItem key={project.id} href={project.url} className={styles.ActionList_LinkItem}>
                {project.title}
              </ActionList.LinkItem>
            ))}
          </ActionList>
        )}
      </ProjectsSection>
    </ErrorBoundary>
  )
}

type EditIssueProjectsSectionProps = {
  issueOrPullRequest: ProjectsSectionFragment$key
  selectedProjectId?: string
  allowedProjectOwner?: string
  onIssueUpdate: () => void
  onRemovedFromProject: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const projectSectionFragment = graphql`
  fragment ProjectsSectionFragment on IssueOrPullRequest
  @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    ... on Issue {
      number
      repository {
        name
        owner {
          login
        }
        isArchived
      }
      id
      projectItems(first: 25, allowedOwner: $allowedOwner, allowSameEnterprise: true)
        @connection(key: "ProjectSection_projectItems") {
        edges {
          node {
            id
            ...ProjectItemSection
            project {
              id
              title
              closed
              number
              url
              viewerCanUpdate
              hasReachedItemsLimit
            }
          }
        }
      }
      viewerCanUpdateMetadata
    }
    ... on PullRequest {
      number
      repository {
        name
        owner {
          login
        }
        isArchived
      }
      id
      projectItems(first: 25, allowedOwner: $allowedOwner, allowSameEnterprise: true)
        @connection(key: "ProjectSection_projectItems") {
        edges {
          node {
            id
            ...ProjectItemSection
            project {
              id
              title
              closed
              number
              url
              viewerCanUpdate
              hasReachedItemsLimit
            }
          }
        }
      }
      viewerCanUpdate
    }
  }
`

export function EditIssueProjectsSection(props: EditIssueProjectsSectionProps) {
  const projectsSectionData = useFragment(projectSectionFragment, props.issueOrPullRequest)

  if (!projectsSectionData.projectItems) return <ProjectsSectionFallback />

  return <EditIssueProjectsSectionInternal projectsSectionData={projectsSectionData} {...props} />
}

type EditIssueProjectsSectionInternalType = {
  projectsSectionData: ProjectsSectionFragment$data
} & EditIssueProjectsSectionProps

const EditIssueProjectsSectionInternal = ({
  projectsSectionData,
  allowedProjectOwner,
  onIssueUpdate,
  onRemovedFromProject,
  insideSidePanel,
  selectedProjectId,
  readonly,
}: EditIssueProjectsSectionInternalType) => {
  const {addToast} = useToastContext()
  const projectItems = (projectsSectionData.projectItems?.edges ?? [])
    .flatMap(edge => (edge?.node ? edge?.node : []))
    .filter(projectItem => projectItem.project.closed !== true)

  const closedProjectItems = (projectsSectionData.projectItems?.edges ?? [])
    .flatMap(edge => (edge?.node ? edge?.node : []))
    .filter(projectItem => projectItem.project.closed === true)

  // Auto-expand when the selected project (e.g. from Projects board side panel) is beyond the visible slice
  // or is in a closed project
  const selectedProjectIsHidden = useMemo(() => {
    if (selectedProjectId === undefined) return false
    const isInClosedProject = closedProjectItems.some(item => item.project.id === selectedProjectId)
    if (isInClosedProject) return true
    return (
      projectItems.length > MAX_VISIBLE_ITEMS &&
      projectItems.findIndex(item => item.project.id === selectedProjectId) >= MAX_VISIBLE_ITEMS
    )
  }, [projectItems, closedProjectItems, selectedProjectId])

  const [showAllProjects, setShowAllProjects] = useState(selectedProjectIsHidden)

  const {viewerCanUpdateMetadata, viewerCanUpdate, repository} = projectsSectionData

  // This can be consolidated once viewerCanUpdateMetadata is implemented in the pull_request model
  const readonlyProject = readonly || !(viewerCanUpdateMetadata || viewerCanUpdate) || repository?.isArchived

  const issueId = projectsSectionData.id || ''
  const hasProjectItems = projectItems.length > 0 || closedProjectItems.length > 0

  const connectionId = ConnectionHandler.getConnectionID(
    issueId, // passed as input to the mutation/subscription
    'ProjectSection_projectItems',
    {
      allowedOwner: allowedProjectOwner,
      allowSameEnterprise: true,
    },
  )

  const environment = useRelayEnvironment()
  const saveProjects = useCallback(
    (selectedProjects: ProjectPickerProject[]) => {
      const projectsToAdd = selectedProjects.filter(project => !projectItems.some(p => p.project.id === project.id))

      const projectsToRemove = projectItems.filter(project => !selectedProjects.some(p => p.id === project.project.id))

      let hasError = false
      for (const project of projectsToAdd) {
        commitUpdateIssueProjectsMutation({
          environment,
          connectionId,
          projectId: project.id,
          issueId,
          onError: () => (hasError = true),
          onCompleted: onIssueUpdate,
        })
      }

      for (const project of projectsToRemove) {
        commitDeleteIssueProjectsMutation({
          environment,
          connectionId,
          projectId: project.project.id,
          itemId: project.id,
          onError: () => (hasError = true),
          onCompleted: () => {
            const isRemovingFromSelectedProject = project.project.id === selectedProjectId
            if (isRemovingFromSelectedProject) {
              onRemovedFromProject()
            }
          },
        })
      }

      if (hasError) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotUpdateProjects,
        })
      }
    },
    [
      addToast,
      connectionId,
      environment,
      issueId,
      onIssueUpdate,
      onRemovedFromProject,
      projectItems,
      selectedProjectId,
    ],
  )

  const selectedProjects = useMemo(() => projectItems.map(projectItem => projectItem.project), [projectItems])

  const sectionHeader = useMemo(() => {
    if (!projectsSectionData.repository || readonlyProject) return <ReadonlyProjectsSectionHeader />

    return (
      <ProjectPicker
        pickerId={`edit-issue-projects-picker-${issueId}`}
        repo={projectsSectionData.repository.name}
        owner={projectsSectionData.repository.owner.login}
        selectedProjects={selectedProjects}
        readonly={readonlyProject}
        onSave={saveProjects}
        anchorElement={props => <ProjectsSectionHeader {...props} />}
        insidePortal={insideSidePanel}
      />
    )
  }, [issueId, projectsSectionData.repository, readonlyProject, saveProjects, selectedProjects, insideSidePanel])

  const visibleProjects = projectItems.slice(0, MAX_VISIBLE_ITEMS)
  const overflowProjects = projectItems.slice(MAX_VISIBLE_ITEMS)
  const totalHiddenCount = overflowProjects.length + closedProjectItems.length
  const hasHiddenItems = totalHiddenCount > 0

  if (!projectsSectionData.repository || !projectsSectionData.number) return null

  return (
    <ProjectsSection sectionHeader={sectionHeader} hasItems={hasProjectItems}>
      <div className={styles.projectItemsContainer}>
        {visibleProjects.map(projectItem => (
          <ProjectItemSection
            key={projectItem.id}
            onIssueUpdate={onIssueUpdate}
            projectItem={projectItem}
            isOpen={selectedProjectId === projectItem.project.id}
            isProjectClosed={projectItem.project.closed}
          />
        ))}
        {showAllProjects && overflowProjects.length > 0 && (
          <div id={PROJECT_OVERFLOW_CONTAINER_ID}>
            {overflowProjects.map(projectItem => (
              <ProjectItemSection
                key={projectItem.id}
                onIssueUpdate={onIssueUpdate}
                projectItem={projectItem}
                isOpen={selectedProjectId === projectItem.project.id}
                isProjectClosed={projectItem.project.closed}
              />
            ))}
          </div>
        )}
        {showAllProjects &&
          closedProjectItems.map(projectItem => (
            <ProjectItemSection
              key={projectItem.id}
              onIssueUpdate={onIssueUpdate}
              projectItem={projectItem}
              isOpen={selectedProjectId === projectItem.project.id}
              isProjectClosed={projectItem.project.closed}
            />
          ))}
      </div>
      {hasHiddenItems && (
        <Button
          variant="invisible"
          block
          alignContent="start"
          trailingVisual={showAllProjects ? ChevronUpIcon : ChevronDownIcon}
          onClick={() => setShowAllProjects(!showAllProjects)}
          className={styles.toggleProjectsButton}
          aria-expanded={showAllProjects}
          aria-controls={showAllProjects ? PROJECT_OVERFLOW_CONTAINER_ID : undefined}
          size="small"
        >
          {showAllProjects
            ? LABELS.showLessProjects
            : LABELS.showMoreProjects(totalHiddenCount, closedProjectItems.length)}
        </Button>
      )}
    </ProjectsSection>
  )
}
